// 로컬 암호화(goals.md 원칙 1). WebCrypto만 쓴다(의존성 0).
// 비밀번호 → PBKDF2-SHA256(고반복) → AES-GCM 256. 키는 메모리에만 두고 저장하지 않는다.
// Argon2id가 더 좋지만 WASM 의존이 생기므로 P0는 PBKDF2 고반복으로 간다(iterations를 meta에 저장해 나중에 올릴 수 있게).

export const PBKDF2_ITERATIONS = 310_000

export interface Cipher {
  iv: string // base64
  ct: string // base64
}

function b64encode(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf)
  let s = ''
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i])
  return btoa(s)
}
function b64decode(s: string): Uint8Array {
  const bin = atob(s)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

export function randomBytes(n: number): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(n))
}

export async function deriveKey(password: string, salt: Uint8Array, iterations = PBKDF2_ITERATIONS): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey('raw', new TextEncoder().encode(password), 'PBKDF2', false, ['deriveKey'])
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: salt as BufferSource, iterations, hash: 'SHA-256' },
    base,
    { name: 'AES-GCM', length: 256 },
    false, // 추출 불가: 키를 밖으로 꺼낼 수 없다
    ['encrypt', 'decrypt'],
  )
}

export async function encryptJSON(key: CryptoKey, value: unknown): Promise<Cipher> {
  const iv = randomBytes(12)
  const data = new TextEncoder().encode(JSON.stringify(value))
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: iv as BufferSource }, key, data)
  return { iv: b64encode(iv), ct: b64encode(ct) }
}

export async function decryptJSON<T = unknown>(key: CryptoKey, cipher: Cipher): Promise<T> {
  const iv = b64decode(cipher.iv)
  const ct = b64decode(cipher.ct)
  const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: iv as BufferSource }, key, ct as BufferSource)
  return JSON.parse(new TextDecoder().decode(plain)) as T
}

export const toB64 = b64encode
export const fromB64 = b64decode
